import dayjs from 'dayjs';
import { h } from 'preact';
import { useMemo } from 'preact/hooks';

import { useTranslation } from '../../../../i18n';
import { NeedAccessButton } from '../../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../../need-access/need-access.interface';
import { buttonize, linkify } from '../../../../utils/a11y.utils';
import type { Post } from '../../../model/post.interface';
import { PerksHelper } from '../../../perks.helper';
import { usePostFilterBar } from '../post-filter-bar.context';
import { useTruncateDescription } from './useTruncateDescripton.hook';

interface Props {
  post: Post;
  previewMode: boolean;
  showPostDate: boolean;
  goToPost: (comments?: boolean) => void;
  collapseLongPosts?: boolean;
}

export const PerksPostBody = ({
  post,
  previewMode,
  showPostDate,
  goToPost,
  collapseLongPosts = true,
}: Props) => {
  const { t } = useTranslation();
  const filterBar = usePostFilterBar();
  const { truncatedDescription, showContReading } = useTruncateDescription({
    post,
    collapseLongPosts,
  });

  const title = useMemo((): string => PerksHelper.getTitle(post), [post]);

  return (
    <div
      className={`single-post-body ${post.hidden || previewMode ? 'post-hidden' : ''} ${showPostDate ? 'has-post-date' : ''}`}
    >
      <h3
        className={`${previewMode ? 'h4 single-text-overflow' : 'h3'}`}
        {...linkify(() => goToPost(false))}
        title={title}
      >
        {title}
      </h3>
      {showPostDate && <p class="post-date">{dayjs(post.publishedAt).format('MMM D, YYYY')}</p>}

      {!post.hidden && !previewMode ? (
        <>
          <p
            className={`post-text-content ${showContReading && post?.userHasAccess ? 'post-text-fade' : ''}`}
            dangerouslySetInnerHTML={{ __html: truncatedDescription }}
          />

          {showContReading && post?.userHasAccess && (
            <p className="post-cont-reading" {...linkify(() => goToPost(false))}>
              {t('perks.continueReading')}
            </p>
          )}

          {showContReading && !post?.userHasAccess && (
            <div className="single-post-cta">
              <NeedAccessButton
                contentType={NeedAccessContentType.POST}
                contentId={post?.id}
                message={t('perks.joinToContinueReading')}
                showLock={true}
              />
            </div>
          )}

          {post.tags && post.tags.length > 0 && (
            <div className="tags-container">
              {post.tags.map((tag, index) =>
                filterBar.visible && filterBar.onOpenFilterModal ? (
                  <span
                    key={index}
                    className="clickable"
                    {...buttonize(() => filterBar.onOpenFilterModal?.(tag.id))}
                  >
                    {tag.name}
                  </span>
                ) : (
                  <span key={index}>{tag.name}</span>
                )
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="ghost-text-bar"></div>
          <div className="ghost-text-bar"></div>
          <div className="ghost-text-bar"></div>
        </>
      )}
    </div>
  );
};
